import React from 'react';
import { Zap, ChevronUp, ChevronDown, Activity, ClipboardList, Minus, History } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

/**
 * QuickActionsPanel - Displays saved quick actions for imaging and referrals
 * 
 * Extracted from App.js to reduce monolithic component size.
 */
export const QuickActionsPanel = ({
  quickActions,
  showQuickActions,
  setShowQuickActions,
  applyQuickActionImaging,
  applyQuickActionReferral,
  removeQuickActionImaging,
  removeQuickActionReferral,
}) => {
  if (quickActions.imaging.length === 0 && quickActions.referrals.length === 0) {
    return null;
  }

  return (
    <Collapsible open={showQuickActions} onOpenChange={setShowQuickActions}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground hover:text-foreground h-8 mb-2">
          <span className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            <span className="text-amber-500 font-medium">Quick Actions</span>
            <Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-500">
              {quickActions.imaging.length + quickActions.referrals.length} saved
            </Badge>
          </span>
          {showQuickActions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 pb-3">
        {quickActions.imaging.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Imaging</Label>
            <div className="flex flex-wrap gap-1.5">
              {quickActions.imaging.map((img, idx) => (
                <div key={idx} className="group relative">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => applyQuickActionImaging(img)}
                    className="h-7 text-xs border-amber-500/30 bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 pr-6"
                    data-testid={`quick-imaging-${idx}`}
                  >
                    <Activity className="w-3 h-3 mr-1" />
                    {img.type}{img.bodyPart ? ` ${(img.bodyPart || '').split(' ')[0]}` : ''}
                  </Button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeQuickActionImaging(idx); }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-amber-500/60 hover:text-destructive transition-opacity"
                    title="Remove from Quick Actions"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {quickActions.referrals.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Referrals</Label>
            <div className="flex flex-wrap gap-1.5">
              {quickActions.referrals.map((spec, idx) => (
                <div key={idx} className="group relative">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => applyQuickActionReferral(spec)}
                    className="h-7 text-xs border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 pr-6"
                    data-testid={`quick-referral-${idx}`}
                  >
                    <ClipboardList className="w-3 h-3 mr-1" />
                    {(spec || 'Referral').split(' ')[0]}
                  </Button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeQuickActionReferral(idx); }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-primary/60 hover:text-destructive transition-opacity"
                    title="Remove from Quick Actions"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground italic pt-1">Click to apply • Hover to remove • Star items below to add more</p>
      </CollapsibleContent>
    </Collapsible>
  );
};

/**
 * SmartTemplatesPanel - Displays AI-generated templates from user history
 * 
 * Extracted from App.js to reduce monolithic component size.
 */
export const SmartTemplatesPanel = ({
  smartTemplates,
  showSmartTemplates,
  setShowSmartTemplates,
  applySmartTemplate,
}) => {
  if (!smartTemplates.templates || smartTemplates.templates.length === 0) {
    return null;
  }

  return (
    <Collapsible open={showSmartTemplates} onOpenChange={setShowSmartTemplates}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground hover:text-foreground h-8 mb-2">
          <span className="flex items-center gap-2">
            <History className="w-4 h-4 text-emerald-500" />
            <span className="text-emerald-500 font-medium">Smart Templates</span>
            <Badge variant="outline" className="text-xs bg-emerald-500/10 border-emerald-500/30 text-emerald-500">from your history</Badge>
          </span>
          {showSmartTemplates ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 pb-3">
        <div className="flex flex-wrap gap-1.5">
          {smartTemplates.templates.map((template, idx) => (
            <Button 
              key={template.id}
              variant="outline" 
              size="sm" 
              onClick={() => applySmartTemplate(template)}
              className={`h-auto py-1.5 px-3 text-xs border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 ${
                template.type === 'auto_generated' ? 'text-emerald-400' : 'text-emerald-500'
              }`}
              title={template.description}
              data-testid={`smart-template-${idx}`}
            >
              <div className="flex flex-col items-start">
                <span className="font-medium">{template.name}</span>
                {template.type === 'auto_generated' && (
                  <span className="text-emerald-500/60 text-[10px]">Combo</span>
                )}
              </div>
            </Button>
          ))}
        </div>
        {smartTemplates.stats && (
          <p className="text-xs text-muted-foreground italic pt-1">
            Based on {smartTemplates.stats.total_documents} documents • Auto-updates with your history
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default { QuickActionsPanel, SmartTemplatesPanel };
